import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuBell, LuBellOff, LuLoaderCircle, LuMegaphone, LuPin, LuSend, LuTrash2 } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import IconButton from '../../components/ui/IconButton';
import { TextArea } from '../../components/form/FormControls';
import { auth, db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { CARD, EmptyState, LoadingRows, SectionHeader } from './components/ProfileUi';

/**
 * "Bildirimler": company announcements pinned for everyone. Admins can pin a
 * new one, delete one, or send a message to every non-admin employee's inbox.
 */
const Notifications = ({ isAdmin }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.notifications.${key}`, vars);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNotification, setNewNotification] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [confirmBroadcast, setConfirmBroadcast] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const notificationsQuery = query(collection(db, 'pinnedNotifications'), orderBy('timestamp', 'desc'));
    return onSnapshot(notificationsQuery, (snapshot) => {
      setNotifications(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Error loading notifications:', error);
      setLoading(false);
    });
  }, []);

  const handleSendNotification = async (event) => {
    event.preventDefault();
    if (!newNotification.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'pinnedNotifications'), {
        text: newNotification,
        senderEmail: auth.currentUser.email,
        timestamp: serverTimestamp(),
      });
      setNewNotification('');
      toast.success(label('toast.pinned'));
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(label('toast.pinError'));
    } finally {
      setSending(false);
    }
  };

  // Yönetici olmayan (rolü boş olanlar dahil) tüm çalışanların gelen kutusuna sistem mesajı
  const handleBroadcastMessage = async () => {
    if (!broadcastMessage.trim()) return;
    setBroadcasting(true);
    try {
      const user = auth.currentUser;
      const allUsersSnapshot = await getDocs(query(collection(db, 'Employees')));
      const nonAdminUsers = allUsersSnapshot.docs.filter((d) => d.data().role !== 'admin');
      await Promise.all(nonAdminUsers.map((d) => addDoc(collection(db, 'messages'), {
        senderEmail: user.email,
        receiverEmail: d.data().eMail,
        text: broadcastMessage,
        timestamp: serverTimestamp(),
        read: false,
        isSystemMessage: true,
      })));
      setBroadcastMessage('');
      setConfirmBroadcast(false);
      toast.success(label('toast.broadcastSent', { count: nonAdminUsers.length }));
    } catch (error) {
      console.error('Detailed error in broadcast message:', error);
      toast.error(label('toast.broadcastError', { message: error.message }));
    } finally {
      setBroadcasting(false);
    }
  };

  const handleDeleteNotification = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'pinnedNotifications', toDelete.id));
      toast.success(label('toast.deleted'));
      setToDelete(null);
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error(label('toast.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date();
    return date.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuBell}
        gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30"
        title={label('title')}
        description={label('description')}
      />

      {isAdmin && (
        <div className="tw-grid tw-gap-3 md:tw-grid-cols-2">
          <form onSubmit={handleSendNotification} className={`${CARD} tw-space-y-3 tw-p-4`}>
            <div className="tw-min-w-0">
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                <LuPin aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-amber-500" />
                {label('admin.pinTitle')}
              </p>
              <p className="tw-mt-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('admin.pinHint')}</p>
            </div>
            <TextArea rows={3} value={newNotification} onChange={(e) => setNewNotification(e.target.value)} placeholder={label('admin.pinPlaceholder')} aria-label={label('admin.pinTitle')} />
            <Button variant="primary" size="sm" type="submit" disabled={sending || !newNotification.trim()}>
              {sending ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSend aria-hidden="true" className="tw-h-4 tw-w-4" />}
              {label('admin.pinSubmit')}
            </Button>
          </form>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (broadcastMessage.trim()) setConfirmBroadcast(true);
            }}
            className={`${CARD} tw-space-y-3 tw-p-4`}
          >
            <div className="tw-min-w-0">
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                <LuMegaphone aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-500" />
                {label('admin.broadcastTitle')}
              </p>
              <p className="tw-mt-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('admin.broadcastHint')}</p>
            </div>
            <TextArea rows={3} value={broadcastMessage} onChange={(e) => setBroadcastMessage(e.target.value)} placeholder={label('admin.broadcastPlaceholder')} aria-label={label('admin.broadcastTitle')} />
            <Button size="sm" type="submit" disabled={broadcasting || !broadcastMessage.trim()}>
              <LuMegaphone aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('admin.broadcastSubmit')}
            </Button>
          </form>
        </div>
      )}

      {loading && <LoadingRows count={3} />}
      {!loading && notifications.length === 0 && <EmptyState icon={LuBellOff} title={label('empty')} text={label('emptyHint')} />}
      {!loading && notifications.length > 0 && (
        <ul className="tw-space-y-2.5">
          {notifications.map((notification) => (
            <li key={notification.id} className={`${CARD} tw-flex tw-items-start tw-gap-3 tw-p-4`}>
              <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-amber-500/10 tw-text-amber-600 dark:tw-text-amber-300">
                <LuPin aria-hidden="true" className="tw-h-4 tw-w-4" />
              </span>
              <div className="tw-min-w-0 tw-flex-1">
                <p className="tw-whitespace-pre-line tw-break-words tw-text-sm tw-leading-relaxed tw-text-slate-800 dark:tw-text-slate-100">{notification.text}</p>
                <p className="tw-mt-1 tw-text-xs tw-tabular-nums tw-text-slate-400 dark:tw-text-slate-500">{formatTime(notification.timestamp)}</p>
              </div>
              {isAdmin && (
                <IconButton label={label('delete')} tone="alert" onClick={() => setToDelete(notification)}>
                  <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={confirmBroadcast}
        title={label('admin.broadcastConfirmTitle')}
        message={label('admin.broadcastConfirm')}
        icon={LuMegaphone}
        tone="warning"
        confirmLabel={label('admin.broadcastSubmit')}
        cancelLabel={t('profilePage.common.cancel')}
        busy={broadcasting}
        onConfirm={handleBroadcastMessage}
        onCancel={() => setConfirmBroadcast(false)}
      />
      <ConfirmDialog
        isOpen={Boolean(toDelete)}
        title={label('deleteTitle')}
        message={label('deleteMessage')}
        icon={LuTrash2}
        tone="danger"
        confirmLabel={label('delete')}
        cancelLabel={t('profilePage.common.cancel')}
        busy={deleting}
        onConfirm={handleDeleteNotification}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
};

Notifications.propTypes = { isAdmin: PropTypes.bool };

export default Notifications;
